import { useEffect, type ReactNode } from 'react'
import BottomSheetContent from '../../../../../components/BottomSheetComponents/BottomSheetContent'
import { SecondaryButton } from '../../../../../components/execution/SecondaryButton'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { mfLogoBaseUrl } from '../../../../../env'
import { formatRupeeLabel } from '../../display-values/lumpsumPlanAmount'
import { EventNames } from '../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import { PlansBottomSheet, PlansExecutionEventIds, PlansScreen } from '../../analytics/plansEvents'
import {
  FolioSourceId,
  SipFolioActionTone,
  type SipFolioRow
} from './folio'

export function SipModifySheetLayout({
  onDismiss,
  mfLogoUrl,
  fundName,
  fundIsin = '',
  heading,
  children
}: {
  onDismiss: () => void
  mfLogoUrl: string
  fundName: string
  fundIsin?: string
  heading: string
  children: ReactNode
}) {
  const logoSrc = mfLogoUrl.length > 0 ? `${mfLogoBaseUrl}${mfLogoUrl}` : ''

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: PlansScreen.SipYourPlanRecommendation,
        eventId: PlansBottomSheet.ModifySip,
        bottomSheetName: PlansBottomSheet.ModifySip,
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
  }, [fundIsin, fundName])

  function handleClose() {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: PlansScreen.SipYourPlanRecommendation,
        eventId: PlansExecutionEventIds.ModifySheetClose,
        buttonLabel: 'Close',
        ...(fundName ? { fundName } : {}),
        ...(fundIsin ? { fundIsin } : {})
      }
    })
    onDismiss()
  }

  return (
    <BottomSheetContent
      onDismiss={handleClose}
      bottomSheetBodyClassName="!px-5 !pb-2 !pt-0"
      bottomSheetHeadClassName="!pt-6"
      headingComponent={
        <div className="flex flex-col pb-4">
          {logoSrc ? (
            <img src={logoSrc} alt="" className="mb-2 h-16 w-16 object-contain ml-3" />
          ) : null}
          <p className="text-[13px] font-[400] leading-[140%] text-[#6A6E6E] px-5">{fundName}</p>
          <h2 className="mt-1 text-[22px] font-[500] leading-[130%] text-[#232323] px-5">{heading}</h2>
        </div>
      }
      headingTitleId="sip-modify-sheet-heading"
      bottomSheetCtas={
        <SecondaryButton
          label="Close"
          onClick={handleClose}
          secondaryButtonClassName="!h-[56px] !text-[16px] !font-[500] !rounded-[12px]"
        />
      }
    >
      <div className="flex flex-col gap-4 pt-2">{children}</div>
    </BottomSheetContent>
  )
}

export function SipFolioRowList({
  rows,
  onPowerUpClick,
  fundName = '',
  fundIsin = ''
}: {
  rows: SipFolioRow[]
  onPowerUpClick?: () => void
  fundName?: string
  fundIsin?: string
}) {
  if (rows.length === 0) {
    return <SipFolioSourcesEmptyCard />
  }

  return (
    <>
      {rows.map((row) => (
        <SipFolioRowCard
          key={row.id}
          row={row}
          fundName={fundName}
          fundIsin={fundIsin}
          onClick={row.id === FolioSourceId.POWERUP ? onPowerUpClick : undefined}
        />
      ))}
    </>
  )
}

export function SipFolioRowCard({
  row,
  onClick,
  fundName = '',
  fundIsin = ''
}: {
  row: SipFolioRow
  onClick?: () => void
  fundName?: string
  fundIsin?: string
}) {
  const title = resolveSipFolioTitle(row.id)
  const isInteractive = row.interactive && onClick != null
  const showCurrent = row.currentAmount > 0
  const actionText = row.actionText
  let actionClassName = 'text-[14px] font-[500] leading-[130%] text-[#232323]'
  if (row.actionTone === SipFolioActionTone.DANGER) {
    actionClassName = 'text-[14px] font-[500] leading-[130%] text-[#E77464]'
  } else if (row.actionTone === SipFolioActionTone.SUCCESS) {
    actionClassName = 'text-[14px] font-[500] leading-[130%] text-[#2A9C4A]'
  }

  let helperNode: ReactNode = row.helperText
  if (row.powerUpRestartSteps != null) {
    helperNode = (
      <PowerUpDecreaseEditSteps
        currentAmount={row.powerUpRestartSteps.currentAmount}
        remainingAmount={row.powerUpRestartSteps.remainingAmount}
      />
    )
  }

  return (
    <div
      className={`rounded-[12px] border border-[#F0F4F7] bg-white p-4 ${isInteractive ? 'cursor-pointer' : ''}`}
      onClick={
        isInteractive
          ? () => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.SipYourPlanRecommendation,
                  eventId: PlansExecutionEventIds.ModifyFolio,
                  buttonLabel: title,
                  ...(fundName ? { fundName } : {}),
                  ...(fundIsin ? { fundIsin } : {})
                }
              })
              onClick?.()
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-[#F0F4F7]">
            <RenderHTML innerHTMLText={resolveSipFolioIcon(row.id)} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-[500] leading-[130%] text-[#232323]">{title}</div>
            {!showCurrent && actionText ? (
              <div className={`mt-[2px] ${actionClassName}`}>{actionText}</div>
            ) : null}
            {showCurrent ? (
              <div className="mt-[2px] text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
                Current : {formatRupeeLabel({ rawAmount: row.currentAmount })}/mo
              </div>
            ) : null}
          </div>
        </div>
        {isInteractive ? (
          <div className="pt-3">
            <RenderHTML innerHTMLText={RightChevron} />
          </div>
        ) : null}
      </div>
      {showCurrent && actionText ? (
        <div className="mt-4 border-t border-[#F0F4F7] pt-4">
          <div className={actionClassName}>{actionText}</div>
          {helperNode ? (
            <div className="mt-2 text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">{helperNode}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function SipFolioSourcesEmptyCard() {
  return (
    <div className="rounded-[12px] border border-[#F0F4F7] bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-[#F0F4F7]">
          <RenderHTML innerHTMLText={FolioUnavailableIcon} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-[500] leading-[130%] text-[#232323]">Folio amounts unavailable</div>
          <div className="mt-[2px] text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
            There's some issue with fetching folio amounts
          </div>
        </div>
      </div>
    </div>
  )
}

export function PowerUpDecreaseEditSteps({
  currentAmount,
  remainingAmount
}: {
  currentAmount: number
  remainingAmount: number
}) {
  return (
    <div>
      <p>Editing SIPs isn&apos;t available on PowerUp yet. Follow these steps :</p>
      <ol className="mt-1 list-decimal pl-5">
        <li>{`Cancel your current ${formatRupeeLabel({ rawAmount: currentAmount })}/mo SIP`}</li>
        <li>{`Start a new SIP at ${formatRupeeLabel({ rawAmount: remainingAmount })}/mo.`}</li>
      </ol>
    </div>
  )
}

export function resolveSipFolioTitle(sourceId: FolioSourceId): string {
  if (sourceId === FolioSourceId.EXTERNAL) {
    return 'External folios'
  }
  if (sourceId === FolioSourceId.INFINITE) {
    return 'Infinite'
  }
  return 'PowerUp'
}

export function resolveSipFolioIcon(sourceId: FolioSourceId): string {
  if (sourceId === FolioSourceId.EXTERNAL) {
    return ExternalFolioIcon
  }
  if (sourceId === FolioSourceId.INFINITE) {
    return InfiniteFolioIcon
  }
  return PowerUpIcon
}

const PowerUpIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
  <rect width="32" height="32" rx="8" fill="#F0F4F7"/>
  <path d="M12.1184 11.0312V16.7425C12.1184 18.0546 11.266 18.4405 9.87135 18.4405C8.47667 18.4405 7.62431 18.0546 7.62431 16.7425V11.0312H5.19629V17.0513C5.19629 19.5596 7.12067 20.5244 9.87135 20.5244C12.622 20.5244 14.5462 19.5596 14.5462 17.0513V11.0312H12.1184Z" fill="#232323"/>
  <path d="M24.1191 12.9049C25.1578 12.9049 25.9998 12.0662 25.9998 11.0316C25.9998 9.99694 25.1578 9.1582 24.1191 9.1582C23.0803 9.1582 22.2383 9.99694 22.2383 11.0316C22.2383 12.0662 23.0803 12.9049 24.1191 12.9049Z" fill="#232323"/>
  <path d="M20.3349 13.3477H20.2112C19.393 13.3477 18.6005 13.6317 17.9698 14.1509L17.8036 14.2877V13.3477H15.6729V22.8408H17.9972V19.6121C18.4494 20.1652 19.3274 20.5126 20.3349 20.5126C22.4398 20.5126 24.1315 19.252 24.1315 16.9108C24.1315 14.6083 22.4398 13.3477 20.3349 13.3477ZM19.8054 18.6345C18.5784 18.6345 17.8036 17.9656 17.8036 16.9108C17.8036 15.8817 18.5784 15.2257 19.8054 15.2257C21.0321 15.2257 21.8069 15.8817 21.8069 16.9108C21.8069 17.9656 21.0321 18.6345 19.8054 18.6345Z" fill="#232323"/>
</svg>`

const ExternalFolioIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
  <rect width="32" height="32" rx="8" fill="#F0F4F7"/>
  <path d="M19.7426 12.2574C17.3995 9.91421 13.6005 9.91421 11.2574 12.2574C8.91419 14.6005 8.9142 18.3995 11.2574 20.7426C13.6005 23.0858 17.3995 23.0858 19.7426 20.7426L20.8033 21.8033C17.8744 24.7322 13.1256 24.7322 10.1967 21.8033C7.26776 18.8744 7.26778 14.1256 10.1967 11.1967C13.1256 8.26777 17.8744 8.26777 20.8033 11.1967L19.7426 12.2574ZM25.5763 15.9697C25.8692 16.2626 25.8692 16.7374 25.5763 17.0303L22.3943 20.2123L21.3336 19.1517L23.2354 17.2499L14.9697 17.2499V15.7501L23.2353 15.7501L21.3336 13.8484L22.3943 12.7877L25.5763 15.9697Z" fill="black"/>
</svg>`

const InfiniteFolioIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M8.43155 0.578134C8.72607 0.337871 9.16069 0.3555 9.43524 0.630049L11.4845 2.67931H14.3817C14.7958 2.67931 15.1313 3.01476 15.1313 3.42889V6.32518L17.1805 8.37444C17.4734 8.66728 17.4734 9.14176 17.1805 9.43459L15.1313 11.4839V14.3829C15.1313 14.797 14.7958 15.1325 14.3817 15.1325H11.4827L9.43524 17.1799C9.1424 17.4727 8.66792 17.4727 8.37508 17.1799L6.32764 15.1325H3.42862C3.01449 15.1325 2.67904 14.797 2.67904 14.3829V11.4839L0.629781 9.43459C0.336948 9.14176 0.336948 8.66728 0.629781 8.37444L2.67904 6.32518V3.42889C2.67904 3.01476 3.01449 2.67931 3.42862 2.67931H6.32582L8.37508 0.630049L8.43155 0.578134ZM7.16647 3.95896C7.02585 4.09959 6.83527 4.17846 6.6364 4.17846H4.17819V6.63576C4.17819 6.83463 4.09932 7.02521 3.95869 7.16583L2.22001 8.90452L3.95869 10.6432C4.09932 10.7838 4.17819 10.9744 4.17819 11.1733V13.6333H6.63822L6.71199 13.637C6.88377 13.6539 7.04518 13.7297 7.1683 13.8528L8.90516 15.5897L10.642 13.8528L10.6967 13.8027C10.8301 13.6934 10.9981 13.6333 11.1721 13.6333H13.6321V11.1733C13.6321 10.9744 13.711 10.7838 13.8516 10.6432L15.5903 8.90452L13.8516 7.16583C13.711 7.02521 13.6321 6.83463 13.6321 6.63576V4.17846H11.1739C10.9751 4.17846 10.7845 4.09959 10.6438 3.95896L8.90516 2.22028L7.16647 3.95896Z" fill="#232323"/>
  <path d="M11.1906 8.85504C11.1906 10.1428 10.1467 11.1866 8.85895 11.1866C7.57124 11.1866 6.52734 10.1428 6.52734 8.85504C6.52734 7.56733 7.57124 6.52344 8.85895 6.52344C10.1467 6.52344 11.1906 7.56733 11.1906 8.85504Z" fill="#232323"/>
</svg>`

const RightChevron = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <g clip-path="url(#clip0_95848_102721)">
    <path d="M9.00045 4.9195L4.08045 0.239504C3.99425 0.157559 3.89276 0.0933947 3.78176 0.0506748C3.67076 0.00795477 3.55244 -0.0124844 3.43354 -0.00947579C3.31464 -0.00646717 3.19751 0.0199303 3.08881 0.0682095C2.98012 0.116489 2.882 0.185704 2.80005 0.271904C2.63456 0.445992 2.545 0.678692 2.55107 0.918814C2.55715 1.15894 2.65836 1.38681 2.83245 1.5523L7.28685 5.7823C7.31641 5.81023 7.33996 5.8439 7.35606 5.88125C7.37215 5.9186 7.38046 5.95884 7.38046 5.9995C7.38046 6.04017 7.37215 6.08041 7.35606 6.11776C7.33996 6.15511 7.31641 6.18878 7.28685 6.2167L2.83245 10.4467C2.65932 10.6111 2.55858 10.8375 2.55239 11.0762C2.5462 11.3148 2.63507 11.5462 2.79945 11.7193C2.96383 11.8924 3.19026 11.9932 3.42892 11.9994C3.66758 12.0056 3.89892 11.9167 4.07205 11.7523L9.01245 7.0603C9.15302 6.91939 9.26429 6.75203 9.33983 6.56788C9.41538 6.38373 9.45369 6.18644 9.45258 5.9874C9.45146 5.78836 9.41093 5.59152 9.33333 5.40823C9.25573 5.22494 9.14259 5.05883 9.00045 4.9195Z" fill="#6A6E6E"/>
  </g>
  <defs>
    <clipPath id="clip0_95848_102721">
      <rect width="12" height="12" fill="white"/>
    </clipPath>
  </defs>
</svg>`

const FolioUnavailableIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect x="4" y="3" width="12" height="14" rx="2" stroke="#6A6E6E" stroke-width="1.4"/>
  <path d="M7 8h6M7 11h4" stroke="#6A6E6E" stroke-width="1.4" stroke-linecap="round"/>
</svg>`
